import { Controller } from 'stimulus'

/**
 * Manages js for the form progress bar (StimulusJS controller)
 *
 * @class FormProgressBarController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'activeStep',
    'dot',
    'stepNumber',
  ]

  /**
   * @function Controllers.FormProgressBarController.initialize
   * @memberof Controllers
   * @description Defines turbolinksLoadCallback for event listener usage
   */
  initialize() {
    this.turbolinksLoadCallback = () => {
      this.update()
      this._doScroll()
    }

    this.baseCss = 'form-progress-bar__trail-dot'
    this.activeCss = `${this.baseCss}--active`
    this.completeCss = `${this.baseCss}--complete`
    this.update()
    document.addEventListener('turbolinks:load', this.turbolinksLoadCallback)
  }

  /**
   * @function Controllers.FormProgressBarController.teardown
   * @memberof Controllers
   * @description Removes event listener from document on teardown.
   */
  teardown() {
    document.removeEventListener('turbolinks:load', this.turbolinksLoadCallback)
  }

  /**
   * @function Controllers.FormProgressBarController.update
   * @memberof Controllers
   * @description Updates form progress bar
   */
  update() {
    const activeStep = parseInt(this.activeStepTarget.value) - 1

    this.stepNumberTarget.innerText = activeStep + 1

    this.dotTargets.forEach((dot, i) => {
      if (i < activeStep) {
        this._complete(dot)
      } else if (i === activeStep) {
        this._active(dot)
      } else {
        this._inactive(dot)
      }
    })
  }

  /**
   * @function Controllers.FormProgressBarController._complete
   * @memberof Controllers
   * @description Styles a dot as complete
   *
   * @param {Element} dot - The dom element for the trail dot
   *
   * @return {undefined} - manipulates DOM
   */
  _complete(dot) {
    dot.classList.add(this.completeCss)
    dot.classList.remove(this.activeCss)
  }


  /**
   * @function Controllers.FormProgressBarController._active
   * @memberof Controllers
   * @description Styles a dot as active
   *
   * @param {Element} dot - The dom element for the trail dot
   *
   * @return {undefined} - manipulates DOM
   */
  _active(dot) {
    dot.classList.add(this.activeCss)
    dot.classList.remove(this.completeCss)
  }


  /**
   * @function Controllers.FormProgressBarController._inactive
   * @memberof Controllers
   * @description Styles a dot as inactive
   *
   * @param {Element} dot - The dom element for the trail dot
   *
   * @return {undefined} - manipulates DOM
   */
  _inactive(dot) {
    dot.classList.remove(this.completeCss)
    dot.classList.remove(this.activeCss)
  }


  /**
   * @function Controllers.FormProgressBarController._doScroll
   * @memberof Controllers
   * @description Scrolls to top of form or first error
   */
  _doScroll() {
    const errors = document.getElementsByClassName('form__errors-list')

    if (errors.length === 0) {
      scrollTo(0, 0)
    } else {
      let previousElement = errors[0].previousSibling
      // skip all previous text nodes
      while (previousElement.nodeName === '#text') {
        previousElement = previousElement.previousSibling
      }

      previousElement.scrollIntoView({ block: 'start', inline: 'start', behavior: 'smooth' })
    }
  }
}
